import React, {useState, useEffect} from "react";

import {PageHeader} from "./PageHeader"
import {DocUploadPanel} from "./DocUploadPanel";
import {CxText} from "../cmx/CxTypography";
import {CxGrid} from "../cmx/CxGrid";
import {CxDataGrid} from "../cmx/CxData";
import {CxConfirmDialog} from "../cmx/CxDialog";
import {CxListFeedback} from "../cmx/CxFeedback";
import {docProjectListOnly, docTaskList, docRead, docDelete} from "../gateway/DocService";
import {Doc} from "../domain/Doc";
import {runningCall, successCall} from "../gateway/CallService";
import {FileUtil} from "../lib/FileUtil";

// ----------------------------------------------------------------------------------------------------------------
// DocPanel
// ----------------------------------------------------------------------------------------------------------------

export function DocPanel ({ projectId, taskId=null, docCount= null,
                            canManage=false, canCollaborate=false}) {

    // states
    const [call, setCall] = useState(runningCall());
    const [openConfirm, setOpenConfirm] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [openUpload, setOpenUpload] = useState(false);

    // view specific settings
    const viewConfig = {
        projectView: {
            title   : "Dokumente",
            desc    : "Die folgenden Dokumente gehören zum Projekt. Unter Arbeitsvorgänge «Anzeige Details» können die Dokumente pro Arbeitsvorgang verwaltet werden.",
            isTask  : false,
        },
        taskView: {
            title   : "Dokumente",
            desc    : "Die folgenden Dokumente gehören zum aktuellen Arbeitsvorgang.",
            isTask  : true,
        }
    }

    const view = taskId ? "taskView" : "projectView";
    const cfg  = viewConfig[view];

    // if rendered, download is always allowed
    const enableUpload   = canCollaborate || canManage;
    const enableDelete   = canManage;

    // load docs
    const shouldLoad = enableUpload || docCount === null || docCount > 0;

    const loadDocs = async () => {
        if (!shouldLoad) {
            setCall(successCall([]));
            return;
        }

        setCall(runningCall());

        const responseCall = taskId
            ? await docTaskList(taskId)
            : await docProjectListOnly(projectId);

        if (responseCall.isSuccess) {
            setCall(successCall(Doc.mapListColumns(responseCall.result)));
        } else {
            setCall(responseCall);
        }
    };

    useEffect(() => {
        if (!projectId) return;
        loadDocs();
    }, [projectId, taskId]);

    //
    // header actions
    //
    const handleLocalActionChange = (value) => {
        if (value === "docUpload") {
            setOpenUpload(true);
        }
    };

    const handleUploadSuccess = (newDoc) => {
        setOpenUpload(false);
        loadDocs();
    };

    const localActionItems = enableUpload ?
        [{ kind: "icon", label: "Hochladen", iconName: "upload", actionName: "docUpload" }] : [];

    //
    // doc actions
    //
    const handleDownloadClick = async (params) => {
        const row = params.row;
        try {
            const responseCall = await docRead(row.id);
            if (!responseCall.isSuccess) throw new Error("Backend call failed");
            const doc = responseCall.result;
            if (!doc.content) throw new Error("No file content");

            const blob = FileUtil.base64ToBlob(doc.content, doc.mime_type);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = row.displayName || "download";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(`Download project doc (id=${row.id}) failed with`, err);
            alert(`Fehler: Das Dokument (Doc Id: ${row.id}) konnte nicht heruntergeladen werden.`);
        }
    };

    const handleDeleteClick = (params) => {
        setDocToDelete(params.row);
        setOpenConfirm(true);
    };

    const handleConfirm = async (action) => {
        setOpenConfirm(false);
        if (action !== "yes" || !docToDelete) return;
        try {
            const responseCall = await docDelete(docToDelete.id);
            if (responseCall.isError) throw new Error("Delete failed");
            loadDocs();
        } catch (err) {
            console.error(`Delete project doc (id=${docToDelete.id}) failed with`, err);
            alert(`Fehler: Das Dokument (Doc Id: ${docToDelete.id}) konnte nicht gelöscht werden.`);
        } finally {
            setDocToDelete(null);
        }
    };

    //
    // init data and show view
    //
    const rows = call.isSuccess ? call.result : [];

    return (
        <>
            <PageHeader
                title={cfg.title}
                isSubtitle={true}
                localActionItems={localActionItems}
                localActionOnChangeHandler={handleLocalActionChange}
            />

            <CxText>{cfg.desc}</CxText>

            <CxListFeedback call={call} />

            { call.isSuccess &&
                <CxGrid size="xl">
                    <CxDataGrid
                        rows={rows}
                        columns={Doc.getListColumns()}
                        onDownloadAction={handleDownloadClick}
                        onDeleteAction={enableDelete ? handleDeleteClick : undefined}
                    />
                </CxGrid>
            }

            <CxConfirmDialog
                open={openConfirm}
                title="Dokument löschen"
                text={docToDelete ? `Soll das Dokument [${docToDelete.displayName}] gelöscht werden?` : ""}
                handleConfirm={handleConfirm}
                severity="Delete"
            />

            { enableUpload && (
                <DocUploadPanel
                    open={openUpload}
                    handleClose={() => setOpenUpload(false)}
                    projectId={projectId}
                    projectTaskId={taskId}
                    onUploadSuccess={handleUploadSuccess}
                />
            )}
        </>
    );
};
